import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ChevronRight, Users2 } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';
import {
  ATTENDANCE_STATUSES,
  type AttendanceResponse,
  type AttendanceStatus,
  type ChildSummary,
} from '../components/me/types';

const searchSchema = z.object({
  studentId: z.string().uuid().optional(),
});

interface ChildrenResponse {
  items: ChildSummary[];
}

export const Route = createFileRoute('/_authed/me/attendance')({
  validateSearch: searchSchema,
  component: MyAttendancePage,
});

function MyAttendancePage() {
  const role = useAuthStore((s) => s.user?.primaryRole);
  const search = Route.useSearch();
  const studentId = search.studentId ?? null;

  // Staff roles get a redirect card instead of the self-service view.
  // Computed as a flag rather than returned early: an early `return` above
  // the hooks below changes this component's hook count between renders,
  // which crashes React with "rendered more hooks than during the previous
  // render".
  //
  // NOT a mount-time concern — `_authed`'s `beforeLoad` awaits `api.me()` and
  // calls `setUser` before any child renders, so `role` is populated on render
  // 1. The flip happens on logout: `reset()` (auth-store) nulls the user
  // synchronously while this page is still mounted, so `role` goes defined ->
  // undefined and the flag goes true -> false. The `role &&` limb keeps that
  // transitional render on the normal body (both queries are already
  // `enabled`-gated to student/parent, so it issues no requests) rather than
  // flashing the staff card on the way to /login.
  const roleUnsupported = Boolean(role) && role !== 'student' && role !== 'parent';

  const isParentMissingPick = role === 'parent' && !studentId;

  const childrenQuery = useQuery({
    queryKey: ['me', 'children'] as const,
    queryFn: ({ signal }) =>
      apiRequest<ChildrenResponse>('/api/me/children', { signal }),
    enabled: role === 'parent',
    retry: false,
  });

  const attendanceQuery = useQuery({
    queryKey: ['me', 'attendance', studentId] as const,
    queryFn: ({ signal }) =>
      apiRequest<AttendanceResponse>('/api/me/attendance', {
        signal,
        ...(studentId ? { query: { studentId } } : {}),
      }),
    enabled: role === 'student' || (role === 'parent' && Boolean(studentId)),
    retry: false,
  });

  if (roleUnsupported) {
    return (
      <ListShell title="My attendance" description="Self-service attendance view.">
        <EmptyState
          title="Not available for your role"
          description="Use the academy attendance pages from your dashboard."
          action={
            <Link to="/dashboard">
              <Button variant="secondary">Back to dashboard</Button>
            </Link>
          }
        />
      </ListShell>
    );
  }

  // Parent without a selected child — show a picker.
  if (isParentMissingPick) {
    return (
      <ListShell title="My attendance" description="Pick a child to view their attendance.">
        {childrenQuery.isError &&
        childrenQuery.error instanceof ApiError &&
        childrenQuery.error.status === 404 ? (
          <ErrorState
            title="Endpoint coming soon"
            description="Parent self-service attendance is waiting on a backend update."
          />
        ) : childrenQuery.isError ? (
          <ErrorState
            title="Could not load your children"
            description={
              childrenQuery.error instanceof ApiError
                ? childrenQuery.error.message
                : 'Please try again in a moment.'
            }
            onRetry={() => childrenQuery.refetch()}
          />
        ) : childrenQuery.isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (childrenQuery.data?.items ?? []).length === 0 ? (
          <EmptyState
            title="No children linked to your household"
            description="Contact the academy if this looks wrong."
            icon={<Users2 size={32} aria-hidden="true" />}
          />
        ) : (
          <ul role="list" className="grid gap-3 sm:grid-cols-2">
            {(childrenQuery.data?.items ?? []).map((c) => (
              <li
                key={c.id}
                className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]"
              >
                <Link
                  to="/me/attendance"
                  search={{ studentId: c.id }}
                  className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-[color:var(--color-surface)]"
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.name}</p>
                    <p className="font-mono text-xs text-[color:var(--color-text-muted)]">
                      {c.admissionNumber}
                    </p>
                  </div>
                  <ChevronRight
                    size={16}
                    aria-hidden="true"
                    className="text-[color:var(--color-text-muted)]"
                  />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </ListShell>
    );
  }

  const isMissingEndpoint =
    attendanceQuery.error instanceof ApiError && attendanceQuery.error.status === 404;

  return (
    <ListShell
      title="My attendance"
      description="Last 60 days of recorded attendance."
      primaryAction={
        role === 'parent' ? (
          <Link to="/me/children">
            <Button variant="ghost" size="sm">
              Change child
            </Button>
          </Link>
        ) : null
      }
    >
      {isMissingEndpoint ? (
        <ErrorState
          title="Endpoint coming soon"
          description="Self-service attendance is waiting on a backend update."
        />
      ) : attendanceQuery.isError ? (
        <ErrorState
          title="Could not load attendance"
          description={
            attendanceQuery.error instanceof ApiError
              ? attendanceQuery.error.message
              : 'Please try again in a moment.'
          }
          onRetry={() => attendanceQuery.refetch()}
        />
      ) : attendanceQuery.isLoading || !attendanceQuery.data ? (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <AttendanceView data={attendanceQuery.data} />
      )}
    </ListShell>
  );
}

// ---------- View ----------

interface AttendanceViewProps {
  data: AttendanceResponse;
}

function AttendanceView({ data }: AttendanceViewProps) {
  const { items, summary } = data;
  return (
    <div className="space-y-6">
      <section
        aria-label="Summary"
        className="grid grid-cols-2 gap-3 sm:grid-cols-5"
      >
        <SummaryStat label="Sessions" value={String(summary.totalSessions)} />
        <SummaryStat label="Attendance" value={`${summary.percent}%`} tone="success" />
        <SummaryStat label="Present" value={String(summary.present)} />
        <SummaryStat label="Late" value={String(summary.late)} tone="warning" />
        <SummaryStat label="Absent" value={String(summary.absent)} tone="danger" />
      </section>

      <section>
        <h2 className="font-display text-base font-semibold">Last 60 days</h2>
        <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
          Each cell is one day. Hover for details.
        </p>
        <div className="mt-3">
          <AttendanceHeatmap items={items} />
        </div>
        <Legend />
      </section>

      <section>
        <h2 className="font-display text-base font-semibold">Recent sessions</h2>
        <RecentList items={items} />
      </section>
    </div>
  );
}

function SummaryStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'success' | 'warning' | 'danger';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-[color:var(--color-success)]'
      : tone === 'warning'
        ? 'text-[color:var(--color-warning)]'
        : tone === 'danger'
          ? 'text-[color:var(--color-danger)]'
          : 'text-[color:var(--color-text)]';
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-3">
      <p className="text-xs text-[color:var(--color-text-muted)]">{label}</p>
      <p className={`mt-0.5 font-display text-2xl font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

// ---------- Heatmap ----------

interface HeatmapProps {
  items: ReadonlyArray<{ date: string; status: AttendanceStatus; batchName: string }>;
}

const HEATMAP_DAYS = 60;

function AttendanceHeatmap({ items }: HeatmapProps) {
  // Build a map of date -> latest status (if multiple sessions in a day, keep the worst).
  const byDate = useMemo(() => {
    const ranking: Record<AttendanceStatus, number> = {
      absent: 4,
      late: 3,
      excused: 2,
      present: 1,
    };
    const map = new Map<string, AttendanceStatus>();
    for (const r of items) {
      const current = map.get(r.date);
      if (!current || ranking[r.status] > ranking[current]) {
        map.set(r.date, r.status);
      }
    }
    return map;
  }, [items]);

  // Build 60 day list, oldest first, with ISO date strings (YYYY-MM-DD).
  const days = useMemo(() => {
    const out: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = HEATMAP_DAYS - 1; i >= 0; i -= 1) {
      const d = new Date(today.getTime() - i * 24 * 3600 * 1000);
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, []);

  return (
    <div
      className="grid gap-1"
      style={{ gridTemplateColumns: 'repeat(8, minmax(0, 1fr))' }}
      role="grid"
      aria-label="Attendance for the last 60 days"
    >
      {days.map((date) => {
        const status = byDate.get(date) ?? null;
        const cellLabel = status
          ? `${formatDate(`${date}T00:00:00Z`)}: ${status}`
          : `${formatDate(`${date}T00:00:00Z`)}: no class`;
        return (
          <div
            key={date}
            role="gridcell"
            aria-label={cellLabel}
            title={cellLabel}
            className={
              'aspect-square w-full rounded-sm border transition-colors ' +
              (status === 'present'
                ? 'bg-[color:var(--color-success)]/30 border-[color:var(--color-success)]/50'
                : status === 'late'
                  ? 'bg-[color:var(--color-warning)]/40 border-[color:var(--color-warning)]/60'
                  : status === 'absent'
                    ? 'bg-[color:var(--color-danger)]/40 border-[color:var(--color-danger)]/60'
                    : status === 'excused'
                      ? 'bg-gold-50 border-gold-300'
                      : 'bg-[color:var(--color-parchment-100)] border-[color:var(--color-border)]')
            }
          />
        );
      })}
    </div>
  );
}

function Legend() {
  return (
    <ul role="list" className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
      {ATTENDANCE_STATUSES.map((s) => (
        <li key={s.value} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={`inline-block h-3 w-3 rounded-sm border ${s.swatch}`}
          />
          <span className="text-[color:var(--color-text-muted)]">{s.label}</span>
        </li>
      ))}
      <li className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="inline-block h-3 w-3 rounded-sm border border-[color:var(--color-border)] bg-[color:var(--color-parchment-100)]"
        />
        <span className="text-[color:var(--color-text-muted)]">No class</span>
      </li>
    </ul>
  );
}

// ---------- Recent list ----------

function RecentList({ items }: { items: ReadonlyArray<HeatmapProps['items'][number] & { notes: string | null }> }) {
  if (items.length === 0) {
    return (
      <EmptyState
        title="No recorded sessions in this window"
        description="Once attendance is logged it will appear here."
      />
    );
  }
  return (
    <ul role="list" className="mt-3 divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      {items.slice(0, 20).map((r, idx) => (
        <li
          key={`${r.date}-${r.batchName}-${idx}`}
          className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium">{r.batchName}</p>
            <p className="text-xs text-[color:var(--color-text-muted)]">
              {formatDate(`${r.date}T00:00:00Z`)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill status={r.status} />
            {r.notes ? (
              <span className="text-xs text-[color:var(--color-text-muted)] max-w-xs truncate">
                {r.notes}
              </span>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function StatusPill({ status }: { status: AttendanceStatus }) {
  const cls = ATTENDANCE_STATUSES.find((s) => s.value === status)?.swatch ?? '';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize text-[color:var(--color-text)] ${cls}`}
    >
      {status}
    </span>
  );
}
